// SPDX-License-Identifier: AGPL-3.0-or-later

import {getCosmeticAssetURL} from '@app/features/cosmetics/utils/CosmeticAssetUtils';
import {cosmeticName} from '@app/features/cosmetics/utils/CosmeticText';
import {mediaUrl} from '@app/features/messaging/utils/MessagingUrlUtils';
import {getPremiumProductName} from '@app/features/premium/utils/PremiumUtils';
import styles from '@app/features/table_game_leaderboard/components/TableGameRewardShowcase.module.css';
import TableGameLeaderboards from '@app/features/table_game_leaderboard/state/TableGameLeaderboards';
import {
	isTableGameChanceKind,
	TABLE_GAME_REWARD_TIERS,
	type TableGameRankedKind,
	type TableGameRewardTier,
} from '@fluxer/constants/src/TableGameConstants';
import type {
	TableGameRewardShowcaseResponse,
	TableGameShowcaseReward,
} from '@fluxer/schema/src/domains/table_game/TableGameRewardSchemas';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CaretDownIcon, CrownIcon, TrophyIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useId, useState} from 'react';

const SEASON_REWARDS_DESCRIPTOR = msg({message: 'Season rewards'});
const PREMIUM_DAYS_DESCRIPTOR = msg({message: '{days, plural, one {# day} other {# days}} {plus}'});
const TIER_LABELS: Record<TableGameRewardTier, string> = {
	rank1: '1.',
	rank2: '2.',
	rank3: '3.',
	top10: '4–10',
	top100: '11–100',
};

export function visibleTiers(showcase: TableGameRewardShowcaseResponse, game: TableGameRankedKind) {
	const hidePremium = isTableGameChanceKind(game) && !showcase.chance_rewards_enabled;
	return TABLE_GAME_REWARD_TIERS.map((tier) => ({
		tier,
		rewards: showcase[tier].filter((reward) => !hidePremium || reward.type !== 'premium_days'),
	})).filter(({rewards}) => rewards.length > 0);
}

function rewardText(reward: TableGameShowcaseReward, i18n: I18n): string {
	switch (reward.type) {
		case 'premium_days':
			return i18n._(PREMIUM_DAYS_DESCRIPTOR, {days: reward.days, plus: getPremiumProductName()});
		case 'cosmetic':
			return cosmeticName(reward.item, i18n.locale);
		case 'badge':
			return reward.badge.description;
		case 'offsite':
			return reward.description;
	}
}

function rewardImageUrl(reward: TableGameShowcaseReward): {src: string; size: number} | null {
	switch (reward.type) {
		case 'cosmetic':
			return {
				src: getCosmeticAssetURL(reward.item.type, {item_id: reward.item.id, asset: reward.item.asset}, 24, false),
				size: 24,
			};
		case 'badge':
			return {src: mediaUrl(`branding/badge-${reward.badge.id}/${reward.badge.icon}.webp?size=128`), size: 16};
		default:
			return null;
	}
}

// The reward's picture: the crown for Plus days, else its item or badge image, left out when it fails to load.
function RewardImage({reward}: {reward: TableGameShowcaseReward}) {
	const [failed, setFailed] = useState(false);
	if (reward.type === 'premium_days') return <CrownIcon className={styles.crown} weight="fill" aria-hidden />;
	const image = rewardImageUrl(reward);
	if (!image || failed) return null;
	return (
		<img
			className={styles.image}
			src={image.src}
			alt=""
			width={image.size}
			height={image.size}
			loading="lazy"
			decoding="async"
			onError={() => setFailed(true)}
		/>
	);
}

// The prize ladder of the current season. Beside a wide board it is always open; above a narrow one it folds behind a
// toggle that names the first prize, closed until opened.
export const TableGameRewardShowcase = observer(function TableGameRewardShowcase({game}: {game: TableGameRankedKind}) {
	const {i18n} = useLingui();
	const headingId = useId();
	const tiersId = useId();
	const [open, setOpen] = useState(false);
	const showcase = TableGameLeaderboards.showcase;
	if (!showcase) return null;
	const tiers = visibleTiers(showcase, game);
	if (tiers.length === 0) return null;
	const title = showcase.title || i18n._(SEASON_REWARDS_DESCRIPTOR);
	return (
		<section
			className={styles.section}
			aria-labelledby={headingId}
			data-open={open || undefined}
			data-flx="table-game-leaderboard.reward-showcase"
		>
			<h2 id={headingId} className={styles.title}>
				<TrophyIcon className={styles.icon} weight="fill" aria-hidden />
				{title}
			</h2>
			<button
				type="button"
				className={styles.toggle}
				aria-expanded={open}
				aria-controls={tiersId}
				onClick={() => setOpen(!open)}
				data-flx="table-game-leaderboard.reward-showcase.toggle"
			>
				<TrophyIcon className={styles.icon} weight="fill" aria-hidden />
				<span className={styles.toggleTitle}>{title}</span>
				<span className={styles.toggleSummary}>{rewardText(tiers[0]!.rewards[0]!, i18n)}</span>
				<CaretDownIcon className={styles.caret} weight="bold" aria-hidden />
			</button>
			<ol id={tiersId} className={styles.tiers}>
				{tiers.map(({tier, rewards}) => (
					<li
						key={tier}
						className={styles.tier}
						data-tier={tier}
						data-flx="table-game-leaderboard.reward-showcase.tier"
					>
						<span className={styles.rank} data-flx="table-game-leaderboard.reward-showcase.rank">
							{TIER_LABELS[tier]}
						</span>
						<ul className={styles.rewards}>
							{rewards.map((reward, index) => (
								<li key={index} className={styles.reward} data-flx="table-game-leaderboard.reward-showcase.reward">
									<RewardImage reward={reward} />
									{rewardText(reward, i18n)}
								</li>
							))}
						</ul>
					</li>
				))}
			</ol>
		</section>
	);
});
